import React, { useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import UsersService from "../services/UsersService";
import Button from "./ui/Button";
import { FormControl } from "./ui/Modal";
import { normalizeMobile } from "../utility/mobileNumber";

// Shared by LoginPopup and SignInPopUp. Google never returns a phone number, but the platform
// keys logins and enquiries on one, so the first time a Google account signs in (or when an
// older Google account still has no real number) the backend answers 422 + `phoneRequired`.
// This swaps the Google button for an inline mobile-number step and re-sends the same Google
// token with the number attached. It's inline on purpose: the popups are Modals, and a second
// fixed-position Modal nested inside one is clipped by the parent dialog.
const GoogleSignInButton = ({ accountType, onSuccess, onError }) => {
  const [credential, setCredential] = useState(null);
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const signIn = async (idToken, phoneNo) => {
    onError("");
    setSubmitting(true);
    try {
      const response = await UsersService.validateGoogleLogin(idToken, accountType, phoneNo);
      setCredential(null);
      setPhone("");
      setPhoneError("");
      onSuccess(response.data);
    } catch (error) {
      const data = error?.response?.data;
      const message = data?.message || (typeof data === "string" ? data : null);
      if (data?.phoneRequired) {
        // First ask carries no error; a rejected number (taken / invalid) explains itself.
        setCredential(idToken);
        setPhoneError(phoneNo ? message || "" : "");
      } else {
        setCredential(null);
        onError(message || "Google sign-in failed. Please try again.");
        console.error("Google sign-in error", error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitPhone = (event) => {
    event.preventDefault();
    const normalized = normalizeMobile(phone);
    if (!normalized) {
      setPhoneError("Enter a valid 10-digit mobile number.");
      return;
    }
    setPhoneError("");
    signIn(credential, normalized);
  };

  if (credential) {
    return (
      <form
        onSubmit={submitPhone}
        className="rounded-xl border border-brand-200 bg-brand-50/60 p-4 text-left"
        data-testid="google-phone-step"
      >
        <p className="text-sm font-semibold text-gray-900">One last step</p>
        <p className="mt-0.5 text-xs text-gray-600">
          Add your mobile number to finish signing in with Google.
        </p>
        <label htmlFor="googleMobile" className="mt-3 block text-sm font-medium text-gray-700">
          Mobile number
        </label>
        <div className="relative mt-1">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
            +91
          </span>
          <FormControl
            id="googleMobile"
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            autoFocus
            placeholder="10-digit mobile number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            isInvalid={phoneError !== ""}
            className="pl-11"
          />
        </div>
        {phoneError && <p className="mt-1 text-sm text-red-600">{phoneError}</p>}
        <div className="mt-3 flex gap-2">
          <Button type="submit" variant="primary" disabled={submitting} className="flex-1">
            {submitting ? "Please wait…" : "Continue"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={submitting}
            onClick={() => {
              setCredential(null);
              setPhone("");
              setPhoneError("");
            }}
          >
            Cancel
          </Button>
        </div>
      </form>
    );
  }

  return (
    <GoogleLogin
      onSuccess={(credentialResponse) => signIn(credentialResponse.credential)}
      onError={() => onError("Google sign-in failed. Please try again.")}
    />
  );
};

export default GoogleSignInButton;
